import type { Account, Id, Party } from '@hero/schema';
import { isIdMatch } from '@hero/schema';

/**
 * Viewer's seat on a bilateral `Account`.
 *
 * - `first`  — viewer is `account.parties[0]`. Balance reads as-is.
 * - `second` — viewer is `account.parties[1]`. Balance flips
 *              sign for "what they owe me".
 * - `none`   — viewer is neither party. UI should treat this as a
 *              permission error (the party-facing API also 403s).
 */
export type AccountViewerRole = 'first' | 'second' | 'none';

/**
 * Frontend mirror of {@link AccountService.getRoleForViewer}.
 *
 * Same canonical-ordering convention as the backend — `parties[0]`
 * is the party at the lower hex `_id`, not "the seller". The signed
 * `balance` is held from `parties[0]`'s POV.
 *
 * Lives in `@hero/core` (not `@hero/web-components`) so admin web
 * UI, the unified dashboard, and shared cards all flatten to the
 * same selector.
 */
export function getAccountRoleForViewer(
  account: Pick<Account, 'parties'> | { parties: Party[] },
  viewerPartyId: Id | string | undefined,
): AccountViewerRole {
  if (!viewerPartyId) return 'none';
  const [first, second] = account.parties;
  if (first && isIdMatch(first._id, viewerPartyId)) return 'first';
  if (second && isIdMatch(second._id, viewerPartyId)) return 'second';
  return 'none';
}

/**
 * Convenience — read `account.balance` from the viewer's POV.
 *
 * Returns a positive number when the counterparty owes the viewer
 * and a negative number when the viewer owes the counterparty. The
 * UI uses the sign to pick label + colour:
 *
 *   > 0  → "Owed to you"  (green)
 *   < 0  → "You owe"      (red)
 *   = 0  → "Settled"      (neutral)
 *
 * Returns `null` if the viewer is not on the account.
 */
export function getAccountBalanceForViewer(
  account: Pick<Account, 'parties' | 'balance'>,
  viewerPartyId: Id | string | undefined,
): number | null {
  const role = getAccountRoleForViewer(account, viewerPartyId);
  if (role === 'none') return null;
  return role === 'first' ? account.balance : -account.balance;
}

/**
 * Resolve the OTHER party on the account given the viewer's id.
 * Used by `AccountCard` / `AccountPage` to render the counterparty
 * name + roles in headers.
 */
export function getAccountCounterparty(
  account: Pick<Account, 'parties'>,
  viewerPartyId: Id | string | undefined,
): Party | null {
  const role = getAccountRoleForViewer(account, viewerPartyId);
  if (role === 'none') return null;
  return role === 'first' ? account.parties[1] : account.parties[0];
}
